import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { MSelectCard } from "@/components/ui/atoms/MSelectCard";
import styles from "../reminder.module.css";
import MTopBar from "@/components/ui/atoms/MTopBar";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import { getMemberInfo } from "@/hooks/useMembers";
import { MButton } from "@/components/ui/atoms/MButtons";
import MFooterNavigation from "@/components/ui/atoms/MFooterNavigation";
import { useReminders } from "@/hooks/useReminders";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { typeAheadService } from "@/services/type-ahead.service";
import {
  CreateReminder,
  ReminderFrequency,
  Reminders,
  ReminderSubType,
  ReminderType,
  UpdateReminder,
} from "@/types/reminders";
import {
  getTimezone,
  initDate,
  inputFieldTime,
  updateTime,
} from "@/utils/date-utils";
import {
  displayEventForField,
  getFrequencyText,
  paintEventDateField,
  paintEventForField as paintEventForField,
  paintEventTimeField,
  paintFrequencyDaysField,
  paintFrequencyDisplayField,
  paintFrequencySelectField,
  paintFrequencySpecificDaysField,
  paintReminderDateField,
  paintReminderEndDateField,
  paintReminderNotesField,
  paintReminderTimeField,
  ReminderInputStage,
} from "../remindersHelper";
import {
  IconDoctor,
  IconLabTest,
  IconTherapySession,
  IconHealthCheckup,
  IconFrequency,
} from "@/components/icons";
import MFrame from "@/components/ui/atoms/MFrame";
import { goBackOneLevel } from "@/utils/nav-utils";
import { DEFAULT_TIME_12HR, DEFAULT_YEARS_AHEAD } from "@/utils/constants";
import { addYears, isAfter, isBefore, isPast } from "date-fns";
import { getErrorMessage } from "@/utils/error";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
  getReminderAppointmentScreenName,
} from "@/constants/analytics.constants";
import { useKeyboardScrollToTop } from "@/hooks/useKeyboardScrollToTop";
import { useFixMobileKeyboard } from "@/hooks/useFixMobileKeyboard";

const CategoryFlow: Record<ReminderSubType, ReminderInputStage[]> = {
  DOCTOR_VISIT: [
    ReminderInputStage.START,
    ReminderInputStage.REMINDER_DETAILS,
    ReminderInputStage.ADD,
  ],
  LAB_TEST: [
    ReminderInputStage.START,
    ReminderInputStage.REMINDER_DETAILS,
    ReminderInputStage.ADD,
  ],
  THERAPY: [
    ReminderInputStage.START,
    ReminderInputStage.FREQUENCY,
    ReminderInputStage.FREQUENCY_DETAILS,
    ReminderInputStage.DETAILS,
    ReminderInputStage.REMINDER_DETAILS,
    ReminderInputStage.ADD,
  ],
  HEALTH_CHECKUP: [
    ReminderInputStage.START,
    ReminderInputStage.REMINDER_DETAILS,
    ReminderInputStage.ADD,
  ],
};

/**
 * Appointment Reminder Creation Page
 * Allows users to select an appointment type to create a reminder for
 */
const AppointmentReminder: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const reminder: Reminders = location.state?.reminder;
  const isEditing = !!reminder;
  const [appointmentType, setAppointmentType] =
    useState<ReminderSubType | null>(reminder?.subType ?? null);
  const [flowIndex, setFlowIndex] = useState<number>(
    isEditing && appointmentType ? CategoryFlow[appointmentType].length - 1 : 0
  );

  // Use keyboard scroll-to-top hook
  // useKeyboardScrollToTop([appointmentType, flowIndex]);

  // Fix mobile keyboard issues
  useFixMobileKeyboard();
  const [eventFor, setEventFor] = useState<string | null>(
    reminder?.reminderFor ?? null
  );
  const [eventDate, setEventDate] = useState<Date | null>(
    initDate(reminder?.eventDate, reminder?.timezone)
  );
  const [eventTime, setEventTime] = useState<string | null>(
    reminder?.eventTime ?? null
  );
  const [reminderDate, setReminderDate] = useState<Date | null>(
    initDate(reminder?.startDate, reminder?.timezone)
  );
  const [reminderTime, setReminderTime] = useState<string | null>(
    inputFieldTime(reminder?.startDate, undefined, false, reminder?.timezone)
  );
  const [reminderEndDate, setReminderEndDate] = useState<Date | null>(
    initDate(reminder?.endDate, reminder?.timezone)
  );
  const [notes, setNotes] = useState<string | null>(reminder?.notes ?? null);
  const [isCritical, setIsCritical] = useState<boolean>(
    reminder ? reminder.tier === "CRITICAL" : false
  );
  const [selectedDays, setSelectedDays] = useState<string[]>(
    reminder?.selectedDays?.map((day) => day.toString()) ?? []
  );
  const [showSetDays, setShowSetDays] = useState<boolean>(false);
  const [frequencyValue, setFrequencyValue] = useState<number>(
    reminder?.frequencyValue ? reminder.frequencyValue : 1
  );
  const { addReminder, updateReminder, totalCount, fetchReminders } =
    useReminders(memberInfo.id);
  const [options, setOptions] = useState<{ value: string }[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [frequency, setFrequency] = useState<ReminderFrequency | null>(
    reminder?.frequency ?? null
  );
  const [reminderTimeError, setReminderTimeError] = useState<string | null>(
    null
  );

  // Fetch reminder count on mount
  useEffect(() => {
    fetchReminders();
  }, []);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_APPOINTMENT,
    });
  }, []);

  useEffect(() => {
    if (appointmentType && appointmentType == ReminderSubType.HEALTH_CHECKUP) {
      setEventFor(t("reminders.HEALTH_CHECKUP.eventFor"));
    }
  }, [appointmentType]);

  const getCategoryKey = (): string => {
    switch (appointmentType) {
      case ReminderSubType.LAB_TEST:
        return "lab_tests_" + memberInfo.derivedMemberType.toLowerCase();
      case ReminderSubType.THERAPY:
        return "therapy_" + memberInfo.derivedMemberType.toLowerCase();
      default:
        return "";
    }
  };

  const getIcon = (type: ReminderSubType, color?: string): React.ReactNode => {
    switch (type) {
      case ReminderSubType.DOCTOR_VISIT:
        return <IconDoctor size={24} color={color} />;
      case ReminderSubType.THERAPY:
        return <IconTherapySession size={24} color={color} />;
      case ReminderSubType.HEALTH_CHECKUP:
        return <IconHealthCheckup size={24} color={color} />;
      case ReminderSubType.LAB_TEST:
        return <IconLabTest size={24} color={color} />;
      default:
        return "";
    }
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setFetched(false);
  };

  const fetchOptions = async () => {
    if (fetched) {
      return;
    }
    try {
      setLoading(true);
      const res = await typeAheadService.searchInCategory(getCategoryKey(), {
        search: query,
      });
      // Convert string array to object array
      setOptions(res.map((item) => ({ value: item })));
      setFetched(true);
    } catch (error) {
      showToast({
        message: "Error fetching vaccination names",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentSelect = (value: ReminderSubType) => {
    if (!value) {
      return;
    }
    resetAllValues();
    setAppointmentType(value);

    // Track appointment type selection
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: getReminderAppointmentScreenName(value, "select"),
    });

    setFlowIndex(0);
    nextStage();
  };

  const resetAllValues = () => {
    setEventFor(null);
    setEventDate(null);
    setEventTime(null);
    setReminderDate(null);
    setReminderTime(null);
    setReminderEndDate(null);
    setNotes(null);
    setSelectedDays([]);
    setShowSetDays(false);
    setFrequencyValue(1);
    setFrequency(null);
    setFetched(false);
    setQuery("");
  };

  const handleBack = () => {
    if (!appointmentType || isEditing) {
      navigate(goBackOneLevel(location.pathname));
      return;
    }
    const flowOffset = getBackFlowOffset();
    const nextStageVal = flowIndex - flowOffset;
    if (nextStageVal < 0) {
      setAppointmentType(null);
    } else {
      setFlowIndex(nextStageVal);
    }
  };

  const validateCurrentStage = (): boolean => {
    switch (CategoryFlow[appointmentType][flowIndex]) {
      case ReminderInputStage.START:
        return !isStartInvalid();
      case ReminderInputStage.DETAILS:
        return !isDetailsInvalid();
      case ReminderInputStage.FREQUENCY:
        return !isFrequencyInvalid();
      case ReminderInputStage.REMINDER_DETAILS:
        return !isReminderDetailsInvalid();
      case ReminderInputStage.FREQUENCY_DETAILS:
        return !isFrequencyDetailsInvalid();
      default:
        return false;
    }
  };

  // Show toast message for error notification
  const showErrorToast = (_: string) => {
    showToast({
      message: t("errors.toast"),
      variant: "error",
    });
  };

  // Frequencies that skip the FREQUENCY_DETAILS step in the therapy wizard.
  const FREQUENCIES_WITHOUT_DETAILS: ReminderFrequency[] = [
    ReminderFrequency.ONLY_ONCE,
    ReminderFrequency.EVERY_DAY,
    ReminderFrequency.EVERY_OTHER_DAY,
  ];

  const getFlowOffset = () => {
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        if (
          frequency &&
          [ReminderInputStage.FREQUENCY].includes(
            CategoryFlow[appointmentType][flowIndex]
          )
        ) {
          if (FREQUENCIES_WITHOUT_DETAILS.includes(frequency)) {
            return 2;
          }
        }
        break;
    }
    return 1;
  };

  const getBackFlowOffset = () => {
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        if (
          frequency &&
          [ReminderInputStage.FREQUENCY, ReminderInputStage.DETAILS].includes(
            CategoryFlow[appointmentType][flowIndex]
          )
        ) {
          if (FREQUENCIES_WITHOUT_DETAILS.includes(frequency)) {
            return 2;
          }
        }
        break;
    }
    return 1;
  };

  const nextStage = () => {
    if (!appointmentType) {
      return;
    }
    const flowIncrement = getFlowOffset();
    const nextStageVal = flowIndex + flowIncrement;
    console.log("CategoryFlow", CategoryFlow[appointmentType]);
    if (nextStageVal > CategoryFlow[appointmentType].length - 1) {
      showErrorToast(t("errors.maxStageReached"));
    } else {
      if (validateCurrentStage()) {
        setFlowIndex(nextStageVal);

        // Track stage changes
        const nextStageType = CategoryFlow[appointmentType][nextStageVal];
        if (nextStageType === ReminderInputStage.REMINDER_DETAILS) {
          // Track date stage
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
            screenName: getReminderAppointmentScreenName(
              appointmentType,
              "date"
            ),
          });
        } else if (nextStageType === ReminderInputStage.ADD) {
          // Track summary stage
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
            screenName: getReminderAppointmentScreenName(
              appointmentType,
              "summary"
            ),
          });
        }

        // Auto-scroll to top when moving to next stage
        setTimeout(() => {
          document
            .querySelector("main")
            ?.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      }
    }
  };

  const paintTypeAheadInput = (
    disabled: boolean = false,
    titleKey?: string
  ) => {
    return (
      <div className="w-full">
        <AutoCompleteInput
          label={t(
            `reminders.${appointmentType}.typeAhead${titleKey ? titleKey : ""}`
          )}
          data={options}
          getOptionLabel={(item) => item.value}
          onSelect={(item) => {
            // Handle both string (from allowCustomInput) and object cases
            if (typeof item === "string") {
              setEventFor(item);
            } else {
              setEventFor(item.value);
            }
          }}
          value={eventFor}
          onChange={(value) => handleQueryChange(value)}
          onSearch={fetchOptions}
          loading={loading}
          placeholder={t(`reminders.${appointmentType}.typeAheadPlaceholder`)}
          allowCustomInput={true}
          minLength={1}
          showAllOnFocus={true}
          disabled={disabled}
        />
      </div>
    );
  };

  // pass a function to check for disable state
  const paintNextButton = (disableCheck: () => boolean) => {
    return (
      <MButton
        variant="filled"
        size="lg"
        onClick={() => nextStage()}
        className={styles.nextButton}
        disabled={disableCheck()}
        text={t("common.next")}
      />
    );
  };

  const eventDateField = (
    labelKey: string = "appointment",
    isSummaryDisplay: boolean = false
  ) => {
    return paintEventDateField(
      eventDate,
      setEventDate,
      t,
      labelKey,
      isSummaryDisplay
    );
  };

  const eventTimeField = (
    labelKey: string = "appointment",
    isSummaryDisplay: boolean = false
  ) => {
    return paintEventTimeField(
      eventDate,
      eventTime,
      setEventTime,
      t,
      labelKey,
      isSummaryDisplay
    );
  };

  const reminderDateField = (
    showLabel: boolean = true,
    labelKey?: string,
    isSummaryDisplay: boolean = false
  ) => {
    return paintReminderDateField(
      reminderDate,
      setReminderDate,
      eventDate,
      t,
      labelKey,
      showLabel,
      isSummaryDisplay
    );
  };

  const reminderEndDateField = (
    showLabel: boolean = true,
    labelKey?: string,
    isSummaryDisplay: boolean = false
  ) => {
    return paintReminderEndDateField(
      reminderEndDate,
      setReminderEndDate,
      addYears(new Date(), DEFAULT_YEARS_AHEAD),
      reminderDate,
      t,
      labelKey,
      showLabel,
      isSummaryDisplay
    );
  };

  const reminderTimeField = (
    labelKey?: string,
    isSummaryDisplay: boolean = false
  ) => {
    return paintReminderTimeField(
      reminderTime,
      setReminderTime,
      reminderDate,
      t,
      labelKey,
      reminderTimeError,
      isSummaryDisplay
    );
  };

  const reminderNotesField = (isSummaryDisplay: boolean = false) => {
    return paintReminderNotesField(
      notes,
      setNotes,
      t,
      undefined,
      isSummaryDisplay,
      styles.textAreaAppointment
    );
  };

  const eventForField = () => {
    return paintEventForField(eventFor, setEventFor, t, appointmentType);
  };

  const paintHeader = (
    title: string,
    description?: string,
    addSpacer: boolean = true
  ) => {
    return (
      <>
        <MHeader
          module="reminders"
          memberInfo={memberInfo}
          title={title}
          description={description ? description : undefined}
          useParentGradient={false}
          className={
            isFinalStage
              ? headerStyles.stickyBelowTopBarWithGradient
              : !appointmentType
              ? headerStyles.stickyBelowTopBarWithFooter
              : headerStyles.stickyBelowTopBar
          }
        />
        {addSpacer && <MSpacer />}
      </>
    );
  };

  const frequencySelectField = () => {
    return paintFrequencySelectField(frequency, handleFrequencySelect, t);
  };

  const handleFrequencySelect = (frequencyToSet: ReminderFrequency) => {
    setFrequency(frequencyToSet);
    if (frequency && frequencyToSet === frequency) {
      nextStage();
    }
  };

  // Auto-advance only when the user picks a frequency from the FREQUENCY step.
  // Without this guard, opening the edit flow seeds `frequency` from the
  // existing reminder and overshoots the final stage, surfacing a stray toast.
  useEffect(() => {
    if (
      frequency &&
      appointmentType &&
      CategoryFlow[appointmentType][flowIndex] === ReminderInputStage.FREQUENCY
    ) {
      nextStage();
    }
  }, [frequency]);

  const handleSetDaysSelect = (options: string[]) => {
    setSelectedDays(options);
    setShowSetDays(false);
  };

  const frequencySpecificDaysField = () => {
    return paintFrequencySpecificDaysField(
      selectedDays,
      setShowSetDays,
      showSetDays,
      handleSetDaysSelect,
      t
    );
  };

  const frequencyDaysField = () => {
    return paintFrequencyDaysField(
      frequencyValue,
      setFrequencyValue,
      t,
      "appointment"
    );
  };

  const frequencyDisplayField = () => {
    return paintFrequencyDisplayField(
      frequency,
      selectedDays,
      frequencyValue,
      t,
      undefined,
      "SummaryDisplay"
    );
  };

  const displayEventField = () => {
    return displayEventForField(
      eventFor,
      t,
      appointmentType + ".add",
      "SummaryDisplay"
    );
  };

  const renderAppointmentDetails = () => {
    if (!eventFor) return null;

    // Add "Dr." prefix for doctor visits
    const displayName =
      appointmentType === ReminderSubType.DOCTOR_VISIT
        ? `Dr. ${eventFor}`
        : eventFor;

    const frequencyText =
      appointmentType === ReminderSubType.THERAPY
        ? getFrequencyText(frequency, selectedDays, frequencyValue, t)
        : null;

    return (
      <div className="flex  gap-3">
        {getIcon(
          appointmentType,
          "var(--brand-colours-indigo-indigo-500, #8764BB)"
        )}
        <div className="flex flex-col gap-[6px]">
          <span
            className="font-semibold leading-[140%]"
            style={{ color: "#45168C" }}
          >
            {displayName}
          </span>
          {frequencyText && (
            <div className="flex items-center gap-1 mt-1">
              <IconFrequency size={12} color="#2A2A2A" />
              <span className="text-xs font-bold" style={{ color: "#2A2A2A" }}>
                {frequencyText}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getStartTitle = () => {
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        return t("reminders.THERAPY.startPrompt");
      case ReminderSubType.LAB_TEST:
        return t("reminders.LAB_TEST.startPrompt");
      default:
        return t("reminders.appointment.startPrompt");
    }
  };

  const getStartDescription = () => {
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        return t("reminders.THERAPY.startDescription");
      case ReminderSubType.LAB_TEST:
        return t("reminders.LAB_TEST.startDescription");
      default:
        return t("reminders.appointment.startDescription");
    }
  };

  const paintStartInput = () => {
    switch (appointmentType) {
      case ReminderSubType.DOCTOR_VISIT:
        return (
          <>
            {eventForField()}
            {eventDateField()}
            {eventTimeField()}
            {reminderNotesField()}
          </>
        );
      case ReminderSubType.HEALTH_CHECKUP:
        return (
          <>
            {eventDateField()}
            {eventTimeField()}
            {reminderNotesField()}
          </>
        );
      case ReminderSubType.THERAPY:
        return <>{paintTypeAheadInput()}</>;
      case ReminderSubType.LAB_TEST:
        return (
          <>
            {paintTypeAheadInput()}
            {eventDateField()}
            {eventTimeField()}
            {reminderNotesField()}
          </>
        );
    }
  };

  const isStartInvalid = (): boolean => {
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        return eventFor === null;
      default:
        return (
          eventFor === null ||
          eventDate === null ||
          (eventTime && isPast(updateTime(eventDate, eventTime)))
        );
    }
  };

  const paintFrequencyInput = () => {
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        return <>{frequencySelectField()}</>;
    }
  };

  const isFrequencyInvalid = (): boolean => {
    return frequency === null;
  };

  const paintFrequencyDetailsInput = () => {
    if (frequency === null) {
      return null;
    }
    switch (frequency) {
      case ReminderFrequency.SPECIFIC_DAYS:
        return <>{frequencySpecificDaysField()}</>;
      case ReminderFrequency.DAY_FREQUENCY:
        return <>{frequencyDaysField()}</>;
    }
  };

  const isFrequencyDetailsInvalid = (): boolean => {
    if (frequency === ReminderFrequency.SPECIFIC_DAYS) {
      return selectedDays.length === 0;
    } else if (frequency === ReminderFrequency.DAY_FREQUENCY) {
      return frequencyValue === 0 || frequencyValue > 60;
    }
    return false;
  };

  const paintDetailsInput = () => {
    switch (appointmentType) {
      default:
        return (
          <>
            {eventDateField("THERAPY")}
            {eventTimeField("THERAPY")}
            {reminderNotesField()}
          </>
        );
    }
  };

  const isDetailsInvalid = (): boolean => {
    if (
      eventDate === null ||
      (eventTime && isPast(updateTime(eventDate, eventTime)))
    ) {
      return true;
    }
    return false;
  };

  const paintReminderDetailsInput = () => {
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        return (
          <>
            {reminderDateField(true, "THERAPY")}
            {frequency !== ReminderFrequency.ONLY_ONCE &&
              reminderEndDateField(true, "appointment")}
            {reminderTimeField("appointment")}
          </>
        );
      default:
        return (
          <>
            {reminderDateField(true, "appointment")}
            {reminderTimeField("appointment")}
          </>
        );
    }
  };

  const isReminderDetailsInvalid = (): boolean => {
    if (reminderDate === null) {
      return true;
    }

    // Check if end date is before start date (skipped for ONLY_ONCE — single event has no end date)
    if (
      [ReminderSubType.THERAPY].includes(appointmentType) &&
      frequency !== ReminderFrequency.ONLY_ONCE &&
      reminderEndDate &&
      reminderDate
    ) {
      if (isBefore(reminderEndDate, reminderDate)) {
        return true;
      }
    }

    if (
      reminderTime === null ||
      (reminderTime && isPast(updateTime(reminderDate, reminderTime)))
    ) {
      return true;
    }

    if (eventTime && eventDate && reminderDate) {
      const eventDate1 = updateTime(eventDate, eventTime);
      const reminderDate1 = updateTime(reminderDate, reminderTime);
      const isInvalid = isAfter(reminderDate1, eventDate1);
      // Only set the error if it's different from current error state
      // to avoid infinite re-renders
      const errorMessage = t("reminders.errors.reminderAfterEventDate");
      if (isInvalid && reminderTimeError !== errorMessage) {
        setReminderTimeError(errorMessage);
      } else if (!isInvalid && reminderTimeError !== null) {
        // Clear the error when validation passes
        setReminderTimeError(null);
      }
      return isInvalid;
    }
    return false;
  };

  const paintAddInput = () => {
    const currentStage = "add";
    switch (appointmentType) {
      case ReminderSubType.HEALTH_CHECKUP:
        return (
          <>
            {eventDateField(currentStage, true)}
            {eventTimeField(currentStage, true)}
            {reminderDateField(true, undefined, true)}
            {reminderTimeField("add", true)}
            {reminderNotesField(true)}
          </>
        );
      case ReminderSubType.THERAPY:
        return (
          <>
            {eventDateField(appointmentType + ".add", true)}
            {eventTimeField(appointmentType + ".add", true)}
            {reminderDateField(true, "add", true)}
            {frequency !== ReminderFrequency.ONLY_ONCE &&
              reminderEndDateField(true, "add", true)}
            {reminderTimeField(currentStage, true)}
            {reminderNotesField(true)}
          </>
        );
      default:
        return (
          <>
            {eventDateField(currentStage, true)}
            {eventTimeField(currentStage, true)}
            {reminderDateField(true, undefined, true)}
            {reminderTimeField(currentStage, true)}
            {reminderNotesField(true)}
          </>
        );
    }
  };

  const isAddInvalid = (): boolean => {
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        return (
          isStartInvalid() ||
          isFrequencyInvalid() ||
          isFrequencyDetailsInvalid() ||
          isDetailsInvalid() ||
          isReminderDetailsInvalid()
        );
      default:
        return (
          isStartInvalid() || isDetailsInvalid() || isReminderDetailsInvalid()
        );
    }
  };

  const handleCreateUpdateReminder = async () => {
    const startDate = updateTime(
      reminderDate,
      reminderTime || DEFAULT_TIME_12HR
    );
    const eventDateTime = updateTime(eventDate, eventTime);
    let endDate = startDate;
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        // ONLY_ONCE has no recurrence: collapse end date onto start date so the
        // backend's window logic treats it as a single-day reminder.
        if (frequency === ReminderFrequency.ONLY_ONCE) {
          endDate = startDate;
        } else {
          endDate = reminderEndDate
            ? updateTime(reminderEndDate, reminderTime || DEFAULT_TIME_12HR)
            : null;
        }
        break;
      default:
        break;
    }
    const selectedDaysValue: number[] =
      appointmentType === ReminderSubType.THERAPY
        ? selectedDays.map((day) => Number(day))
        : [];

    // Get current browser timezone
    const timezone = getTimezone();

    if (isEditing && reminder?.id) {
      // Update existing reminder
      const updatedReminder: UpdateReminder = {
        type: ReminderType.APPOINTMENT,
        subType: appointmentType,
        startDate,
        endDate,
        reminderFor: eventFor,
        eventDate: eventDateTime,
        eventTime,
        frequency,
        selectedDays: selectedDaysValue,
        frequencyValue,
        notes,
        timezone,
        tier: isCritical ? "CRITICAL" : "ROUTINE",
      };

      await updateReminder(reminder.id, updatedReminder);

      // Track reminder completion for edit case
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: getReminderAppointmentScreenName(
          appointmentType,
          "complete"
        ),
      });
    } else {
      // Check if this is first reminder BEFORE adding
      const isFirstReminder = totalCount === 0;

      // Create new reminder
      const newReminder: CreateReminder = {
        type: ReminderType.APPOINTMENT,
        subType: appointmentType,
        startDate,
        endDate,
        reminderFor: eventFor,
        eventDate: eventDateTime,
        eventTime,
        frequency,
        selectedDays: selectedDaysValue,
        frequencyValue,
        notes,
        timezone,
        tier: isCritical ? "CRITICAL" : "ROUTINE",
      };

      try {
        await addReminder(newReminder);

        // Track reminder completion with first reminder flag
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: getReminderAppointmentScreenName(
            appointmentType,
            "complete"
          ),
          ...(isFirstReminder && { action: "first_reminder_added" }),
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err, "Failed to create reminder");
        showErrorToast(errorMessage);
        return;
      }
    }

    navigate("/reminders");
  };

  const isFinalStage =
    (appointmentType &&
      CategoryFlow[appointmentType][flowIndex] === ReminderInputStage.ADD) ||
    isEditing;
  return (
    <div
      className={`flex flex-col min-h-screen relative ${
        isFinalStage ? "pb-[125px]" : "pb-[24px]"
      } ${isFinalStage ? styles.addStageBackgroundFinal : ""}`}
    >
      <MTopBar
        hideProfilePicker
        exitPath={appointmentType ? "/reminders" : undefined}
        exitKey="appointment"
        withBackground={!isFinalStage}
        showPurpleGradient={isFinalStage}
        onBack={handleBack}
      />

      <div
        className="profile-page-root flex flex-col "
        style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
      >
        <div className={styles.content}>
          {!appointmentType && (
            <>
              <div className={styles.cardContainer}>
                {paintHeader(
                  t("reminders.appointment.typeLabel"),
                  t("reminders.appointment.description")
                )}
                <div className={styles.optionsContainer}>
                  {Object.values(ReminderSubType).map((option) => (
                    <MSelectCard
                      key={option}
                      text={t(`reminders.${option}.title`)}
                      selected={appointmentType === option}
                      onClick={() => handleAppointmentSelect(option)}
                      leftIcon={getIcon(option)}
                      variant="description"
                      description={t(`reminders.${option}.description`)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
          {appointmentType &&
            CategoryFlow[appointmentType][flowIndex] ===
              ReminderInputStage.START && (
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(getStartTitle(), getStartDescription())}
                <div className="profile-input-container flex-1">
                  {paintStartInput()}
                </div>
                {/* Next button at the bottom right - following Figma design */}
                <div className="confirm-footer pt-0">
                  {paintNextButton(isStartInvalid)}
                </div>
              </div>
            )}
          {appointmentType &&
            CategoryFlow[appointmentType][flowIndex] ===
              ReminderInputStage.FREQUENCY && (
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  t("reminders.appointment.frequencyPrompt"),
                  t("reminders.appointment.frequencyPromptDetails")
                )}
                <div className="profile-input-container flex-1">
                  {paintFrequencyInput()}
                </div>
                <div className="confirm-footer pt-0">
                  {paintNextButton(isFrequencyInvalid)}
                </div>
              </div>
            )}
          {appointmentType &&
            CategoryFlow[appointmentType][flowIndex] ===
              ReminderInputStage.DETAILS && (
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  t("reminders.appointment.startPrompt"),
                  t("reminders.appointment.startDescription")
                )}
                <div className="profile-input-container flex-1">
                  {paintDetailsInput()}
                </div>
                {/* Next button at the bottom right - following Figma design */}
                <div className="confirm-footer pt-0">
                  {paintNextButton(isDetailsInvalid)}
                </div>
              </div>
            )}
          {appointmentType &&
            CategoryFlow[appointmentType][flowIndex] ===
              ReminderInputStage.FREQUENCY_DETAILS && (
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  t(
                    `reminders.appointment.${frequency}.frequencyDetailsPrompt`
                  ),
                  t(
                    `reminders.appointment.${frequency}.frequencyDetailsDescription`
                  )
                )}
                <div className="profile-input-container flex-1">
                  {paintFrequencyDetailsInput()}
                </div>
                {/* Next button at the bottom right - following Figma design */}
                <div className="confirm-footer pt-0">
                  {paintNextButton(isFrequencyDetailsInvalid)}
                </div>
              </div>
            )}
          {appointmentType &&
            CategoryFlow[appointmentType][flowIndex] ===
              ReminderInputStage.REMINDER_DETAILS && (
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  t(`reminders.appointment.detailsPrompt`),
                  t(`reminders.appointment.detailsDescription`)
                )}
                <div className="profile-input-container flex-1">
                  {paintReminderDetailsInput()}
                </div>
                {/* Next button at the bottom right - following Figma design */}
                <div className="confirm-footer pt-0">
                  {paintNextButton(isReminderDetailsInvalid)}
                </div>
              </div>
            )}
          {appointmentType &&
            CategoryFlow[appointmentType][flowIndex] ===
              ReminderInputStage.ADD && (
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  t(`reminders.appointment.addTitle`),
                  t(`reminders.appointment.addDescription`),
                  false
                )}
                <div className="profile-input-container flex-1">
                  <MFrame className="items-start py-6 px-5 mb-[-22px]">
                    {renderAppointmentDetails()}
                  </MFrame>
                  <MFrame className="">
                    <div
                      className={`w-full flex flex-col gap-[24px] px-[8px] pt-[0px] pb-[8px]`}
                    >
                      {paintAddInput()}
                    </div>
                  </MFrame>
                </div>
                <div className="confirm-footer">
                  <MButton
                    text={t("reminders.setReminder")}
                    variant="filled"
                    size="lg"
                    onClick={() => handleCreateUpdateReminder()}
                    className="w-full"
                    disabled={isAddInvalid()}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
      {!appointmentType && <MFooterNavigation />}
    </div>
  );
};

export default AppointmentReminder;
